import React, { useEffect, useState, useMemo, useRef } from 'react';
import { api } from '../api';
import BouquetCard from './BouquetCard';
import FilterChips from './FilterChips';
import { maxAvailableOverHorizon } from '../utils/bouquetAvailability';
import { useCatalogHorizon } from '../catalogHorizon';
import { useCatalogLabels, genitivePlural } from '../catalogLabels';
import { SectionLoader } from './NavIcons';
import UnavailableNotice from './UnavailableNotice';
import { useShopTimezone } from '../utils/timezone';
import { getDeepLinkTarget, takeDeepLink, replaceCategoryUrl, leaveLandingUrl } from '../utils/deepLink';
import { loadItemInfo, pickSimilarBouquets, bouquetDisplayName } from '../utils/unavailable';
import { trackGoal } from '../utils/metrika';

function BouquetCatalog({ cart, addBouquetToCart, updateBouquetQuantity }) {
  const catalogLabels = useCatalogLabels();
  const horizon = useCatalogHorizon();   // «Горизонт планирования каталога» из настроек
  // Окна букетов считаются по «сегодня» магазина — до прихода его пояса из настроек не считаем
  const shopTz = useShopTimezone();
  const [bouquets, setBouquets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [supplyWindows, setSupplyWindows] = useState({}); // product_id -> [{from, until, free}] окна запаса
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const categorySectionRefs = useRef({});
  // Блок «Сейчас нет в продаже» / «Такой страницы нет» наверху каталога для букета по ссылке
  // (ТЗ «реклама по наличию», В1): { id, status, note, name, image, category, similarIds }
  const [notice, setNotice] = useState(null);
  const noticeRef = useRef(null);
  const skipCategoryScrollRef = useRef(false);

  const cartByBouquetId = useMemo(() => {
    const map = {};
    (cart || []).forEach(it => {
      if (it.kind === 'bouquet') map[it.bouquet_id || it.id] = it;
    });
    return map;
  }, [cart]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      api.getBouquets({ limit: 200 }),
      api.getCategories('bouquets').catch(() => []),
    ]).then(([data, cats]) => {
      if (!mounted) return;
      setBouquets(data.items || []);
      setSupplyWindows(data.supply_windows || {});
      setCategories(cats || []);
      setLoading(false);
    }).catch(() => {
      if (mounted) setLoading(false);
    });
    return () => { mounted = false; };
  }, []);

  // Скролл к секции при выборе категории (паттерн из MenuPage).
  useEffect(() => {
    if (skipCategoryScrollRef.current) {
      skipCategoryScrollRef.current = false;
      return;
    }
    if (activeCategory) {
      const el = categorySectionRefs.current[activeCategory];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeCategory]);

  // Пришли по ссылке на раздел букетов (/catalog/… или c_<id> из бота) — выбираем его
  useEffect(() => {
    if (loading) return;
    const t = getDeepLinkTarget();
    if (!t || t.kind !== 'category') return;
    const cat = categories.find(c => c.id === t.id);
    if (cat && takeDeepLink('category', cat.id)) setActiveCategory(cat.slug);
  }, [loading, categories]);

  // Пришли по ссылке на букет, которого нет в каталоге (скрыт, нечего собрать ни сегодня, ни на
  // горизонте, в архиве), — блок «Сейчас нет в продаже» с букетами, которые можно собрать сегодня.
  // Нет такого букета — «Такой страницы нет». Букет в каталоге открывает своё окно сам (BouquetCard).
  useEffect(() => {
    if (loading) return undefined;
    const t = getDeepLinkTarget();
    if (!t || t.kind !== 'bouquet' || bouquets.some(b => b.id === t.id)) return undefined;
    if (!takeDeepLink('bouquet', t.id)) return undefined;
    let alive = true;
    loadItemInfo('bouquet', t.id).then(info => {
      if (!alive) return;
      const missing = info.state === 'missing';
      trackGoal('item_unavailable', { kind: missing ? 'missing' : 'bouquet', item_id: t.id });
      // Похожие фиксируем при открытии: с корзиной меняются только цифры на карточках
      const shown = bouquets.filter(b => categories.some(c => c.slug === b.category?.slug));
      setNotice({
        id: Date.now(),
        status: missing ? 'Такой страницы нет' : 'Сейчас нет в продаже',
        note: missing ? 'Ссылка устарела или в ней опечатка' : null,
        name: missing ? null : bouquetDisplayName(info.name, info.flowwowName) || null,
        image: missing ? null : info.image,
        category: missing ? null : info.category,
        similarIds: pickSimilarBouquets(shown, missing ? {} : {
          categoryId: info.category ? info.category.id : null, excludeId: t.id,
        }).map(b => b.id),
      });
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const handleCategoryChange = (slug) => {
    const next = activeCategory === slug ? null : slug;
    setActiveCategory(next);
    // На сайте адрес раздела — в строке браузера (им можно поделиться)
    replaceCategoryUrl(next ? categories.find(c => c.slug === next) : null);
  };

  // Группировка букетов по категориям (паттерн из MenuPage.groupProductsByCategories).
  const bouquetGroups = useMemo(() => {
    if (!shopTz) return [];
    // Per-date доступность с учётом окон запаса (текущий+плановые) и занятости корзиной.
    // Дата заказа выбирается на чекауте → лимит «+» = max доступности по горизонту,
    // «сегодня N» и «с DATE M» — информационные бейджи (см. bouquetAvailability.js).
    const enrichBouquet = (b) => {
      const cartQty = cartByBouquetId[b.id]?.quantity || 0;
      const selfIndex = (cart || []).findIndex(
        it => it.kind === 'bouquet' && (it.bouquet_id || it.id) === b.id
      );
      const a = maxAvailableOverHorizon(b, supplyWindows, cart, bouquets, selfIndex, horizon);
      return {
        ...b,
        effective_today: a.today,                  // «В наличии сегодня N» (cart-aware)
        effective_limit: a.limit,                  // лимит «+» (max по достижимым датам)
        effective_future_date: a.futureDate,       // карточка: «с DATE: M» (cart-aware)
        effective_future_capacity: a.futureCapacity,
        // «занято корзиной»: сегодня было бы доступно (todayRaw>0), но корзина съела до 0.
        // Естественный разрыв стока (todayRaw===0) бейджем «занято» НЕ помечаем.
        occupied_by_cart: a.todayRaw > 0 && a.today === 0,
        // Backend-сырые today_available и next_better_date НЕ перезаписываем — они нужны для
        // СТАБИЛЬНОЙ сортировки каталога (иначе карточки прыгают при каждом +/−).
        blocked_by_cart: a.limit === 0 && cartQty === 0,
        cart_qty: cartQty,
      };
    };

    const groups = {};
    categories.forEach(cat => {
      groups[cat.slug] = { category: cat, bouquets: [] };
    });
    bouquets.forEach(b => {
      const slug = b.category?.slug;
      if (slug && groups[slug]) groups[slug].bouquets.push(enrichBouquet(b));
    });
    // Сортировка по СЫРЫМ backend-значениям (today_available, next_better_date) — не
    // меняется при добавлении в корзину, поэтому карточки не прыгают местами.
    // Карточные числа (effective_*) обновляются, а позиции остаются стабильными.
    Object.values(groups).forEach(g => {
      g.bouquets.sort((a, bx) => {
        const aHas = (a.today_available || 0) > 0;
        const bHas = (bx.today_available || 0) > 0;
        if (aHas !== bHas) return aHas ? -1 : 1;
        if (aHas) return (bx.today_available || 0) - (a.today_available || 0);
        const aDate = a.next_better_date || '9999-99-99';
        const bDate = bx.next_better_date || '9999-99-99';
        return aDate.localeCompare(bDate);
      });
    });
    return Object.values(groups).filter(g => g.bouquets.length > 0);
    // horizon и shopTz приходят из настроек асинхронно — без них карточки не пересчитались бы
  }, [bouquets, categories, cart, supplyWindows, cartByBouquetId, horizon, shopTz]);

  const categoriesWithBouquets = bouquetGroups.map(g => g.category);

  // Блок стоит прямо над разделом букета, если раздел есть в каталоге, иначе — наверху
  const noticeCategory = notice && notice.category
    ? categoriesWithBouquets.find(c => c.id === notice.category.id) || null
    : null;
  const noticeAtTop = !noticeCategory;

  // Блок показан и разделы посчитаны (их ждут пояс магазина из настроек): адрес — раздела (или
  // главной, без записи истории), раздел выбран, блок на экране. Один раз на блок.
  const noticeId = notice ? notice.id : null;
  const noticeHandledRef = useRef(null);
  useEffect(() => {
    if (!noticeId || !shopTz || noticeHandledRef.current === noticeId) return;
    noticeHandledRef.current = noticeId;
    leaveLandingUrl(noticeCategory);
    if (noticeCategory && noticeCategory.slug !== activeCategory) {
      skipCategoryScrollRef.current = true;
      setActiveCategory(noticeCategory.slug);
    }
    try {
      if (noticeCategory && noticeRef.current) noticeRef.current.scrollIntoView({ block: 'start' });
      else window.scrollTo(0, 0);
    } catch (e) { /* jsdom */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noticeId, shopTz]);

  let noticeEl = null;
  if (notice) {
    const enriched = new Map(bouquetGroups.flatMap(g => g.bouquets).map(b => [b.id, b]));
    noticeEl = (
      <UnavailableNotice
        status={notice.status}
        note={notice.note}
        name={notice.name}
        image={notice.image}
        onClose={() => { trackGoal('item_unavailable_close', {}); setNotice(null); }}
        noticeRef={noticeRef}
      >
        {notice.similarIds.map(id => enriched.get(id)).filter(Boolean).map(b => (
          <BouquetCard
            key={b.id}
            bouquet={b}
            cartItem={cartByBouquetId[b.id]}
            addBouquetToCart={(...args) => { trackGoal('item_unavailable_add', { item_id: b.id }); return addBouquetToCart(...args); }}
            updateBouquetQuantity={updateBouquetQuantity}
            compact
          />
        ))}
      </UnavailableNotice>
    );
  }

  if (loading || !shopTz) {
    return (
      <div className="products-container">
        <SectionLoader />
      </div>
    );
  }

  if (bouquets.length === 0) {
    // Раздел переименовывается владельцем («Букеты» → «Сеты»), поэтому текст
    // строим от настройки; без известной формы падежа — нейтральная фраза.
    const genitive = genitivePlural(catalogLabels.bouquets);
    return (
      <div className="products-container" style={{ textAlign: 'center', color: '#6B6B68' }}>
        <div style={{ fontSize: 60, marginBottom: 10 }}>💐</div>
        <div style={{ fontSize: 16 }}>{genitive ? `${genitive} пока нет` : 'Здесь пока пусто'}</div>
        <div style={{ fontSize: 13, marginTop: 8 }}>Загляните позже</div>
        {noticeEl}
      </div>
    );
  }

  return (
    <>
      {categoriesWithBouquets.length > 0 && (
        <FilterChips
          categories={categoriesWithBouquets}
          activeCategory={activeCategory}
          setActiveCategory={handleCategoryChange}
        />
      )}
      <div className="products-container">
        {noticeAtTop && noticeEl}
        {bouquetGroups.map(group => (
          <div key={group.category.slug} style={{ marginBottom: '32px' }}>
            {!noticeAtTop && noticeCategory.id === group.category.id && noticeEl}
            <h2
              ref={el => { if (el) categorySectionRefs.current[group.category.slug] = el; }}
              style={{
                fontSize: '20px',
                fontWeight: '600',
                marginBottom: '16px',
                // без своего отступа: левый край — как у карточек и чипов (владелец 29.10)
                color: '#000',
                // Под липкой шапкой ещё липкая полоса категорий — её высоту меряет
                // FilterChips (--chips-height). Раньше здесь стояло 56px, высота полосы
                // у Стемса, и у Букетберри заголовок уезжал под чипы.
                scrollMarginTop: 'calc(var(--header-height, 120px) + var(--chips-height, 56px) + 8px)',
              }}
            >
              {group.category.name}
            </h2>
            <div className="products-grid bouquets-grid">
              {group.bouquets.map(b => (
                <BouquetCard
                  key={b.id}
                  bouquet={b}
                  cartItem={cartByBouquetId[b.id]}
                  addBouquetToCart={addBouquetToCart}
                  updateBouquetQuantity={updateBouquetQuantity}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export default BouquetCatalog;
